import { ChangeDetectionStrategy, Component, computed, inject, input, output, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { Store } from '@ngrx/store';
import { TagsAutocompleteComponent } from '@src/suite/common/controls/tags/tags-autocomplete';
import { OPTION_ALL } from '@src/suite/common/enums/constants';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { FilterSavedState } from '@src/suite/common/store/inline-filter/inline-filter.models';
import { adjustFilterDefinition, initializeFilterDefinition } from '@src/suite/common/utils/filter-utils';
import { SORT_BY_FILTER_ID, TODAY_ONLY_FILTER_ID } from '@src/suite/common/utils/section-filter-utils';

import { CheckListComponent } from '../../../controls/check-list/check-list';
import { StaffIDListMultipleComponent } from '../../../controls/staff-id-list/staff-id-list-multiple';

@Component({
    selector: 'st-section-filter',
    imports: [CheckListComponent, MatButtonToggle, MatButtonToggleGroup, MatDivider, MatIcon, MatIconButton, StaffIDListMultipleComponent, TagsAutocompleteComponent],
    templateUrl: './section-filter.html',
    styleUrl: './section-filter.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-section-filter',
    },
})
export class SectionFilterComponent {
    private store = inject(Store);
    readonly languageSettings = this.store.selectSignal(selectI18N);

    readonly config = input<StorytellingFilterDefinition[]>([]);
    readonly state = input<FilterSavedState>({});
    readonly disabled = input<boolean>(false);

    readonly stateChange = output<FilterSavedState>();
    readonly save = output<void>();
    readonly revert = output<void>();
    readonly clear = output<void>();

    readonly mergedFilters = computed<StorytellingFilterDefinition[]>(() => {
        const cfg = this.config();
        const state = this.state() ?? {};
        const languageSettings = this.languageSettings();
        return cfg.map(def => {
            const initialized = { ...def };
            const value = state[def.ID];
            if (def.ID === TODAY_ONLY_FILTER_ID || def.ID === SORT_BY_FILTER_ID) {
                initialized.Value = value ?? OPTION_ALL;
            } else {
                initializeFilterDefinition(initialized, languageSettings ?? {}, value);
            }
            return initialized;
        });
    });

    onChange(value: unknown, filter: StorytellingFilterDefinition) {
        this.emitValue(filter.ID, value);
    }

    onSelectionChange(value: unknown, filter: StorytellingFilterDefinition) {
        this.emitValue(filter.ID, adjustFilterDefinition(filter, this.languageSettings() ?? {}, value).Value);
    }

    onToggleChange(event: MatButtonToggleChange, filter: StorytellingFilterDefinition) {
        this.emitValue(filter.ID, event.value);
    }

    onSave() {
        this.save.emit();
    }

    onRevert() {
        this.revert.emit();
    }

    onClear() {
        this.clear.emit();
    }

    private emitValue(id: string, value: unknown) {
        const current = this.state() ?? {};
        this.stateChange.emit({ ...current, [id]: value });
    }
}
